type AnyObj = Record<string, any>;

export type RuleContext = {
  promptData?: AnyObj;
  proyecto?: AnyObj;
};

const hasItems = (arr: any) => Array.isArray(arr) && arr.length > 0;

export type RuleKey =
  | "guia_aprendizaje"
  | "guia_aprendizajell"
  | "actividades_aplicacion"
  | "autoevaluacion"
  | "planificacion_clase_activa"
  | "planificacion"
  | "concepciones_erroneas"
  | "conocimientos_previos"
  | "design_thinking"
  | "conexiones_realidad"
  | "informe_desempeno"
  | "ruleta_juegos"
  | "cotejo"
  | "aprendizaje_ludica"
  | "mensaje_apoderado"
  | "preguntas_pensamiento_critico"
  | "test"
  | "contestar_correo"
  | "rubrica"
  | "tareas_hogar"
  | "ticket_salida"
  | "abp_beta"
  | "unidad_tematica";

// Helper: para reglas “normales”, permite que la data venga de promptData o proyecto
const getData = (ctx: RuleContext) => ctx.promptData ?? ctx.proyecto ?? {};

export const SEND_BUTTON_RULES: Record<RuleKey, (ctx: RuleContext) => boolean> = {
  actividades_aplicacion: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  autoevaluacion: (ctx) => {
    const d = getData(ctx);
    const cnOk =
      !!d.nivel_cn &&
      !!d.materia_cn &&
      !!d.tipo_evaluacion &&
      !!d.formato &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_cn);
    const orgOk =
      !!d.nivel_org &&
      !!d.materia_org &&
      !!d.tipo_evaluacion &&
      !!d.formato &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_org);
    const tpOk =
      !!d.nivel_tp &&
      !!d.materia_tp &&
      !!d.tipo_evaluacion &&
      !!d.formato &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  planificacion_clase_activa: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  planificacion: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tiempo_clase && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  concepciones_erroneas: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  conocimientos_previos: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  design_thinking: (ctx) => {
    const d = getData(ctx);
    const cnOk =
      !!d.nivel_cn &&
      !!d.materia_cn &&
      !!d.tema_problematica &&
      !!d.horas_semanales &&
      !!d.cantidad_de_semanas &&
      hasItems(d.objetivos_aprendizaje_cn);
    const orgOk =
      !!d.nivel_org &&
      !!d.materia_org &&
      !!d.tema_problematica &&
      !!d.horas_semanales &&
      !!d.cantidad_de_semanas &&
      hasItems(d.objetivos_aprendizaje_org);
    const tpOk =
      !!d.nivel_tp &&
      !!d.materia_tp &&
      !!d.tema_problematica &&
      !!d.horas_semanales &&
      !!d.cantidad_de_semanas &&
      hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  conexiones_realidad: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  guia_aprendizaje: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  guia_aprendizajell: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  informe_desempeno: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.titulo && !!d.periodo;
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.titulo && !!d.periodo;
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.titulo && !!d.periodo;
    return cnOk || orgOk || tpOk;
  },

  ruleta_juegos: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.edad && !!d.interes && !!d.superheroe && !!d.lugar;
    const orgOk = !!d.edad && !!d.interes && !!d.superheroe && !!d.lugar;
    const tpOk = !!d.edad && !!d.interes && !!d.superheroe && !!d.lugar;
    return cnOk || orgOk || tpOk;
  },

  cotejo: (ctx) => {
    const d = getData(ctx);
    const cnOk =
      !!d.nivel_cn &&
      !!d.materia_cn &&
      !!d.tipo_actividad &&
      !!d.titulo &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_cn);
    const orgOk =
      !!d.nivel_org &&
      !!d.materia_org &&
      !!d.tipo_actividad &&
      !!d.titulo &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_org);
    const tpOk =
      !!d.nivel_tp &&
      !!d.materia_tp &&
      !!d.tipo_actividad &&
      !!d.titulo &&
      !!d.descripcion_actividad &&
      hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  aprendizaje_ludica: (ctx) => {
    const d = getData(ctx);
    const cnOk =
      !!d.nivel_cn &&
      !!d.materia_cn &&
      !!d.tema &&
      !!d.edad &&
      !!d.cantidad_estudiantes &&
      !!d.duracion &&
      hasItems(d.objetivos_aprendizaje_cn);
    const orgOk =
      !!d.nivel_org &&
      !!d.materia_org &&
      !!d.tema &&
      !!d.edad &&
      !!d.cantidad_estudiantes &&
      !!d.duracion &&
      hasItems(d.objetivos_aprendizaje_org);
    const tpOk =
      !!d.nivel_tp &&
      !!d.materia_tp &&
      !!d.tema &&
      !!d.edad &&
      !!d.cantidad_estudiantes &&
      !!d.duracion &&
      hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  mensaje_apoderado: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.destinatario && !!d.asunto && !!d.objetivo && !!d.accion_requerida;
    const orgOk = !!d.destinatario && !!d.asunto && !!d.objetivo && !!d.accion_requerida;
    const tpOk = !!d.destinatario && !!d.asunto && !!d.objetivo && !!d.accion_requerida;
    return cnOk || orgOk || tpOk;
  },

  preguntas_pensamiento_critico: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  test: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema_evaluacion && !!d.cantidad_tiempo && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema_evaluacion && !!d.cantidad_tiempo && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema_evaluacion && !!d.cantidad_tiempo && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  contestar_correo: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.destinatario && !!d.asunto && !!d.correoOriginal && !!d.ideasPrincipales;
    const orgOk = !!d.destinatario && !!d.asunto && !!d.correoOriginal && !!d.ideasPrincipales;
    const tpOk = !!d.destinatario && !!d.asunto && !!d.correoOriginal && !!d.ideasPrincipales;
    return cnOk || orgOk || tpOk;
  },

  rubrica: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tipo_actividad && !!d.nivel_desempeno && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tipo_actividad && !!d.nivel_desempeno && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tipo_actividad && !!d.nivel_desempeno && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  tareas_hogar: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  ticket_salida: (ctx) => {
    const d = getData(ctx);
    const cnOk = !!d.nivel_cn && !!d.materia_cn && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_cn);
    const orgOk = !!d.nivel_org && !!d.materia_org && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_org);
    const tpOk = !!d.nivel_tp && !!d.materia_tp && !!d.tema_evaluacion && hasItems(d.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },

  abp_beta: (ctx) => {
    const p = ctx.proyecto ?? {}; 
    const cnOk = !!p.nivel_curso_cn && !! hasItems(p.materias_cn )  && hasItems(p.objetivos_aprendizaje_cn) && !!p.tema;
    const orgOk = !!p.nivel_curso_org && !!hasItems(p.materias_org )  && hasItems(p.objetivos_aprendizizaje_org ?? p.objetivos_aprendizaje_org) && !!p.tema;
    const tpOk = !!p.nivel_curso_tp && !!hasItems(p.materias_tp )  && hasItems(p.objetivos_aprendizaje_tp) && !!p.tema;
    return cnOk || orgOk || tpOk;
  },

  unidad_tematica: (ctx) => {
    const p = ctx.proyecto ?? {}; 
    const cnOk = !!p.nivel_curso_cn && !! hasItems(p.materias_cn )  && hasItems(p.objetivos_aprendizaje_cn);
    const orgOk = !!p.nivel_curso_org && !!hasItems(p.materias_org )  && hasItems(p.objetivos_aprendizizaje_org ?? p.objetivos_aprendizaje_org);
    const tpOk = !!p.nivel_curso_tp && !!hasItems(p.materias_tp )  && hasItems(p.objetivos_aprendizaje_tp);
    return cnOk || orgOk || tpOk;
  },
};

export function isSendButtonDisabled(ruleKey: RuleKey, ctx: RuleContext): boolean {
  return !SEND_BUTTON_RULES[ruleKey](ctx);
}
